import { Button } from '@/components/ui/Button';
import { Drawer } from '@/components/ui/Drawer';
import { mdiDelete, mdiPencil } from '@mdi/js';
import * as React from 'react';

const formatDate = (date: string) =>
  new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

interface EntityDrawerViewProps<TEntity, TFormData> {
  isOpen: boolean;
  entity: TEntity | null;
  mode: 'view' | 'create' | 'edit';
  isLoading: boolean;
  isFormLoading: boolean;
  entityLabel: string;
  spinnerColor?: string;
  onClose: () => void;
  onEdit: (entity: TEntity) => void;
  onDelete: (entity: TEntity) => void;
  onFormSubmit: (data: TFormData) => Promise<void>;
  getTitle: (
    entity: TEntity | null,
    mode: 'view' | 'create' | 'edit',
  ) => string;
  renderHeader: (entity: TEntity) => React.ReactNode;
  renderDetails: (entity: TEntity) => React.ReactNode;
  renderExtraSections?: (entity: TEntity) => React.ReactNode;
  renderForm: (props: {
    entity: TEntity | null;
    onSubmit: (data: TFormData) => Promise<void>;
    onCancel: () => void;
    loading: boolean;
  }) => React.ReactNode;
  getId: (entity: TEntity) => number | string;
  getCreatedAt: (entity: TEntity) => string | undefined;
  getUpdatedAt: (entity: TEntity) => string | undefined;
}

export const EntityDrawerView = <TEntity, TFormData>({
  isOpen,
  entity,
  mode,
  isLoading,
  isFormLoading,
  entityLabel,
  spinnerColor = 'border-coffice-yellow',
  onClose,
  onEdit,
  onDelete,
  onFormSubmit,
  getTitle,
  renderHeader,
  renderDetails,
  renderExtraSections,
  renderForm,
  getId,
  getCreatedAt,
  getUpdatedAt,
}: EntityDrawerViewProps<TEntity, TFormData>) => {
  if (isLoading) {
    return (
      <Drawer isOpen={isOpen} onClose={onClose} title='Loading...' width='md'>
        <div className='p-6 flex items-center justify-center'>
          <div
            className={`animate-spin rounded-full h-8 w-8 border-b-2 ${spinnerColor}`}
          ></div>
        </div>
      </Drawer>
    );
  }

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={getTitle(entity, mode)}
      width='md'
    >
      {mode === 'view' && entity && (
        <div className='p-6 space-y-6'>
          {renderHeader(entity)}

          <div className='grid grid-cols-1 gap-6'>
            <div className='bg-grey-50 rounded-lg p-4'>
              <h4 className='text-sm font-medium text-grey-700 mb-3'>
                {entityLabel} Information
              </h4>
              <dl className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <div>
                  <dt className='text-xs text-grey-500'>{entityLabel} ID</dt>
                  <dd className='text-sm font-mono text-grey-900'>
                    {getId(entity)}
                  </dd>
                </div>
                {renderDetails(entity)}
                <div>
                  <dt className='text-xs text-grey-500'>Created</dt>
                  <dd className='text-sm text-grey-900'>
                    {getCreatedAt(entity)
                      ? formatDate(getCreatedAt(entity)!)
                      : 'N/A'}
                  </dd>
                </div>
                {getUpdatedAt(entity) && (
                  <div>
                    <dt className='text-xs text-grey-500'>Last Updated</dt>
                    <dd className='text-sm text-grey-900'>
                      {formatDate(getUpdatedAt(entity)!)}
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            {renderExtraSections?.(entity)}

            <div className='bg-grey-50 rounded-lg p-4'>
              <h4 className='text-sm font-medium text-grey-700 mb-3'>
                Quick Actions
              </h4>
              <div className='flex flex-wrap gap-2'>
                <Button
                  icon={mdiPencil}
                  label={`Edit ${entityLabel}`}
                  color='secondary'
                  small
                  onClick={() => onEdit(entity)}
                />
                <Button
                  icon={mdiDelete}
                  label={`Delete ${entityLabel}`}
                  color='danger'
                  small
                  outline
                  onClick={() => onDelete(entity)}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {(mode === 'create' || mode === 'edit') &&
        renderForm({
          entity,
          onSubmit: onFormSubmit,
          onCancel: onClose,
          loading: isFormLoading,
        })}
    </Drawer>
  );
};
